import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import straivLogo from '@/assets/straiv-logo-share.png';
import { ShareableWorkout } from '../ShareWorkoutCard';
import { PlatformMap } from '../PlatformMap';

interface LiveRouteCaptureProps {
  workout: ShareableWorkout;
}

const formatTimeExact = (totalSeconds: number) => {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const secRounded = Math.round(s);
  const secStr = Math.abs(s - secRounded) < 0.01 ? String(secRounded).padStart(2, '0') : s.toFixed(1);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${secStr}`;
  return `${m}:${secStr}`;
};

const formatPaceExact = (paceSeconds: number) => {
  if (!paceSeconds || !isFinite(paceSeconds) || paceSeconds <= 0) return '--:--';
  const m = Math.floor(paceSeconds / 60);
  const s = Math.round(paceSeconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const formatTimeFromMinutes = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = Math.floor(minutes % 60);
  const s = Math.round((minutes % 1) * 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
};

const formatShareTitle = (title: string) => {
  if (!title?.trim()) return title;
  const s = title
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .trim();
  return s.split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
};

/**
 * `LiveRouteCapture` is the view we render BEHIND the share-modal preview
 * slot so a native WKWebView screenshot can capture the LIVE Apple MapKit JS
 * basemap. The static `ShareTemplateRoute` is HTML the JS can rasterise;
 * this is HTML the *system* rasterises so the WebGL map ends up in the
 * resulting PNG.
 *
 * Layout matches the run-summary "Expand" map: the basemap fills the
 * container edge-to-edge, with a top scrim (logo + run title) and a bottom
 * scrim (stats card) for legibility.
 *
 * The component fills its (positioned) parent. ShareWorkoutModal mounts it
 * inside the preview slot at a 9:16 share-friendly aspect, then asks the
 * native plugin to snapshot only that rect - the rest of the modal stays
 * exactly as the user sees it, no full-screen flash.
 *
 * All sizes use container query units (cqw) so the design built around an
 * iPhone-sized 390pt-wide canvas scales fluidly down to the smaller modal
 * preview without any JS measurement.
 */
export const LiveRouteCapture = forwardRef<HTMLDivElement, LiveRouteCaptureProps>(
  ({ workout }, ref) => {
    const { t } = useTranslation();
    const routeCoordinates = workout.route_coordinates || [];
    const distanceKm = workout.distance_km ?? 0;
    const paceSecondsPerKm = workout.pace_seconds_per_km;
    const durationSeconds = workout.duration_seconds;

    const routePoints: [number, number][] = routeCoordinates.map(c => [c.lat, c.lng]);
    const center: [number, number] | null =
      routePoints.length > 0
        ? routePoints[Math.floor(routePoints.length / 2)]
        : null;

    return (
      <div
        ref={ref}
        data-live-route-capture="true"
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          background: '#000',
          overflow: 'hidden',
          // All children below use cqw (1cqw = 1% of THIS element's width)
          // so the design scales fluidly inside the modal preview slot.
          containerType: 'inline-size',
        }}
      >
        {/* Fullscreen Apple Maps basemap with the route overlay - same setup
            as the run-summary Expand view, minus the per-km markers. */}
        <div style={{ position: 'absolute', inset: 0 }}>
          {routePoints.length >= 2 && (
            <PlatformMap
              key="live-route-capture"
              center={center}
              routePoints={routePoints}
              showUserMarker={false}
              showRoute={true}
              showStartFinishMarkers={true}
              showKmMarkers={false}
              fitToRoute={true}
              className="w-full h-full"
              darkMode={true}
              interactive={false}
              autoFollow={false}
              zoom={14}
            />
          )}
        </div>

        {/* Top scrim */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '32%',
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0) 100%)',
            pointerEvents: 'none',
          }}
        />

        {/* Top label - logo + title */}
        <div
          style={{
            position: 'absolute',
            top: '4cqw',
            left: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '0 6cqw',
          }}
        >
          {/* See ShareTemplateGymExercises for why we crop with a wrapper. */}
          <div
            style={{
              width: '56cqw',
              // 380:110 ratio of the visible logo strip
              aspectRatio: '380 / 110',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <img
              src={straivLogo}
              alt="STRAIV"
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </div>
          <div
            style={{
              fontSize: '7.2cqw',
              fontWeight: 700,
              color: '#0AFFC2',
              lineHeight: 1.1,
              marginTop: '1.5cqw',
              textAlign: 'center',
              textShadow: '0 0.5cqw 4cqw rgba(0,0,0,0.75)',
            }}
          >
            {formatShareTitle(workout.title)}
          </div>
        </div>

        {/* Bottom scrim */}
        <div
          aria-hidden
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '38%',
            background:
              'linear-gradient(0deg, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.65) 50%, rgba(0,0,0,0) 100%)',
            pointerEvents: 'none',
          }}
        />

        {/* Stats card */}
        <div
          style={{
            position: 'absolute',
            left: '5cqw',
            right: '5cqw',
            bottom: '6cqw',
          }}
        >
          <div
            style={{
              background: 'linear-gradient(180deg, rgba(10,30,25,0.78) 0%, rgba(5,16,14,0.88) 100%)',
              border: '0.4cqw solid rgba(10, 255, 194, 0.28)',
              borderRadius: '6cqw',
              padding: '5cqw 5.5cqw',
              backdropFilter: 'blur(2cqw)',
              WebkitBackdropFilter: 'blur(2cqw)',
              boxShadow: '0 4cqw 10cqw rgba(0,0,0,0.55)',
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '3cqw',
              textAlign: 'center',
            }}
          >
            <StatBlock label={t('components.shareTemplates.distance')} value={String(distanceKm || '0')} unit="km" />
            <StatBlock
              label={t('components.shareTemplates.pace')}
              value={paceSecondsPerKm != null ? formatPaceExact(paceSecondsPerKm) : (workout.average_pace || '--:--')}
              unit="/km"
            />
            <StatBlock
              label={t('components.shareTemplates.time')}
              value={durationSeconds != null ? formatTimeExact(durationSeconds) : formatTimeFromMinutes(workout.duration_minutes)}
              unit="\u00A0"
            />
            <StatBlock
              label={t('components.shareTemplates.calories')}
              value={String(workout.calories_burned || '--')}
              unit="kcal"
            />
          </div>
          <p
            style={{
              fontSize: '2.8cqw',
              letterSpacing: '0.25cqw',
              textAlign: 'center',
              color: 'rgba(255,255,255,0.55)',
              marginTop: '3cqw',
              textShadow: '0 0.25cqw 1.5cqw rgba(0,0,0,0.7)',
            }}
          >
            {t('components.shareTemplatesExtra.poweredBy')}
          </p>
        </div>
      </div>
    );
  }
);

LiveRouteCapture.displayName = 'LiveRouteCapture';

const StatBlock = ({ label, value, unit }: { label: string; value: string; unit: string }) => (
  <div>
    <p
      style={{
        fontSize: '2.7cqw',
        textTransform: 'uppercase',
        letterSpacing: '0.3cqw',
        color: 'rgba(255,255,255,0.6)',
        lineHeight: 1,
        margin: 0,
      }}
    >
      {label}
    </p>
    <p
      style={{
        fontSize: '6.6cqw',
        fontWeight: 700,
        color: '#0AFFC2',
        lineHeight: 1.15,
        margin: '0.8cqw 0 0',
      }}
    >
      {value}
    </p>
    <p style={{ fontSize: '2.5cqw', color: 'rgba(255,255,255,0.5)', margin: 0 }}>{unit}</p>
  </div>
);
